import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import DetailedCreatorProfile from "@/components/profile/detailed-creator-profile";
import { RedFlagModal } from "@/components/modals/red-flag-modal";
import { RedFlagBadge } from "@/components/ui/red-flag-badge";
import type { Creator } from "@shared/schema";
import { TrendingUp, Flag } from "lucide-react";

interface CreatorModalContextType {
  openCreatorModal: (creator: Creator) => void;
  closeCreatorModal: () => void;
  selectedCreator: Creator | null;
  isOpen: boolean;
}

const CreatorModalContext = createContext<CreatorModalContextType | undefined>(undefined);

interface CreatorModalProviderProps {
  children: ReactNode;
}

export function CreatorModalProvider({ children }: CreatorModalProviderProps) {
  const [selectedCreator, setSelectedCreator] = useState<Creator | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [redFlagModalOpen, setRedFlagModalOpen] = useState(false);

  const openCreatorModal = useCallback((creator: Creator) => {
    setSelectedCreator(creator);
    setIsOpen(true);
  }, []);

  const closeCreatorModal = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => setSelectedCreator(null), 300);
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      closeCreatorModal();
    }
  }, [closeCreatorModal]);

  return (
    <CreatorModalContext.Provider value={{ openCreatorModal, closeCreatorModal, selectedCreator, isOpen }}>
      {children}
      
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
          <DialogHeader className="flex flex-row items-center justify-between gap-4 pr-8">
            <div className="flex items-center gap-2">
              <DialogTitle>Profil du Createur</DialogTitle>
              {selectedCreator?.isRedFlag && (
                <RedFlagBadge 
                  reason={selectedCreator.redFlagReason} 
                  note={selectedCreator.redFlagNote}
                  size="md"
                />
              )}
            </div>
            {selectedCreator && (
              <Button
                variant={selectedCreator.isRedFlag ? "outline" : "ghost"}
                size="sm"
                onClick={() => setRedFlagModalOpen(true)}
                className={selectedCreator.isRedFlag ? "text-destructive border-destructive" : ""}
                data-testid="button-open-redflag-modal"
              >
                <Flag className="h-4 w-4 mr-1" />
                {selectedCreator.isRedFlag ? "Modifier signal" : "Signaler"}
              </Button>
            )}
          </DialogHeader>
          {selectedCreator && (
            <div className="w-full">
              <div className="overflow-y-auto max-h-[70vh] mb-6">
                <DetailedCreatorProfile creator={selectedCreator} />
                
                <div className="mt-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center space-x-2">
                        <TrendingUp className="h-5 w-5" />
                        <span>Historique d'activite</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Aucune activite recente
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {selectedCreator && (
        <RedFlagModal
          open={redFlagModalOpen}
          onOpenChange={setRedFlagModalOpen}
          creatorId={selectedCreator.id}
          creatorName={selectedCreator.name}
          isCurrentlyFlagged={selectedCreator.isRedFlag || false}
          currentReason={selectedCreator.redFlagReason}
          currentNote={selectedCreator.redFlagNote}
        />
      )}
    </CreatorModalContext.Provider>
  );
}

export function useCreatorModal() {
  const context = useContext(CreatorModalContext);
  if (context === undefined) {
    throw new Error("useCreatorModal must be used within a CreatorModalProvider");
  }
  return context;
}
